import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { API_BASE_URL } from '../../utils/api';
import BackButton from '../../components/BackButton';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import DataTable from 'react-data-table-component';
import Modal from 'react-modal';
const ShiftList = () => {
  const [shifts, setShifts] = useState([]);
  const [clients, setClients] = useState([]);
  const [filterDate, setFilterDate] = useState('all');
  const [filterClient, setFilterClient] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [searchText, setSearchText] = useState('');
  const navigate = useNavigate();
  const { id: clientId } = useParams();
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (clientId) setFilterClient(clientId);
  }, [clientId]);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API_BASE_URL}/api/clients`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(res => setClients(res.data)).catch(() => console.error('Failed to fetch clients'));
  }, [token]);

  useEffect(() => {
    if (!token) return;
    const fetchShifts = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/shifts`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        let filtered = res.data;
        if (filterDate === 'today') {
          const today = dayjs().format('YYYY-MM-DD');
          filtered = filtered.filter(s => s.date === today);
        } else if (filterDate === 'week') {
          const start = dayjs().startOf('week');
          const end = dayjs().endOf('week');
          filtered = filtered.filter(s =>
            dayjs(s.date).isAfter(start.subtract(1, 'day')) &&
            dayjs(s.date).isBefore(end.add(1, 'day'))
          );
        }
        if (filterClient) {
          filtered = filtered.filter(s => s.clientId === parseInt(filterClient));
        }

        filtered.sort((a, b) => {
          const aTime = dayjs(`${a.date} ${a.startTime}`);
          const bTime = dayjs(`${b.date} ${b.startTime}`);
          return aTime - bTime;
        });

        setShifts(filtered);
      } catch {
        toast.error('Failed to load shifts');
      }
    };
    fetchShifts();
  }, [filterDate, filterClient, token]);
  const findClient = (clientId) => {
    return clients.find((client) => client.id === clientId);
  };
  const handleDeleteShift = async (id) => {
    if (!window.confirm('Delete this shift?')) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/shifts/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Deleted');
      setShifts(prev => prev.filter(s => s.id !== id));
    } catch {
      toast.error('Delete failed');
    }
  };

  const columns = [
    {
      name: 'Shift Info',
      sortable: true,
      width: '115px',
      cell: (row) => {
        return (
          <span
            className="hover:text-blue-500 cursor-pointer flex items-center gap-1"
            onClick={() => navigate(`/shifts/${row.id}/note`)}
          >
            {row.title || 'Untitled'}
            <span role="img" aria-label="info">🛈</span>
          </span>
        );
      },
    },
    {
      name: 'Client Name',
      sortable: true,
      width: '116px',
      cell: (row) => {
        const client = findClient(row.clientId);
        return (
          <span
            className="hover:text-blue-500 cursor-pointer flex items-center gap-1"
            onClick={() => setSelectedClient(client)}
          >
            {client?.name || 'N/A'}
            <span role="img" aria-label="user">👤</span>
          </span>
        );
      },
    },
    { name: 'Date', selector: row => row.date, sortable: true, width: '220px' },
    { name: 'Start Time', selector: row => row.startTime, sortable: true, width: '220px' },
    { name: 'End Time', selector: row => row.endTime, sortable: true, width: '220px' },
    {
      name: 'Actions',
      width: '380px',
      cell: row => (
        <div className="flex gap-2">
          <button
            onClick={() => navigate(`/shifts/${row.id}/note`)}
            className="bg-[#8F4EFF] hover:bg-purple-700 text-white px-2 py-1 rounded text-sm"
          >
            + Note
          </button>
          <button
            onClick={() => navigate(`/shifts/${row.id}/edit`)}
            className="bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded text-sm"
          >
            Edit
          </button>
          <button
            onClick={() => handleDeleteShift(row.id)}
            className="bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded text-sm"
          >
            Delete
          </button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true
    }
  ];

  const filteredShifts = shifts.filter(shift =>
    (shift.Client?.name || '').toLowerCase().includes(searchText.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#0E0A1F] text-white p-6">
      <BackButton />
      <div className="max-w-7xl mx-auto">
        <h1 className="text-2xl font-bold mb-4">Dashboard (<small>Booked Shifts</small>)</h1>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-4">
          <button
            className={`px-4 py-2 rounded ${filterDate === 'today' ? 'bg-[#8F4EFF]' : 'bg-[#2A1C4A] hover:bg-purple-600'}`}
            onClick={() => setFilterDate('today')}
          >Today</button>
          <button
            className={`px-4 py-2 rounded ${filterDate === 'week' ? 'bg-[#8F4EFF]' : 'bg-[#2A1C4A] hover:bg-purple-600'}`}
            onClick={() => setFilterDate('week')}
          >This Week</button>
          <button
            className={`px-4 py-2 rounded ${filterDate === 'all' ? 'bg-[#8F4EFF]' : 'bg-[#2A1C4A] hover:bg-purple-600'}`}
            onClick={() => setFilterDate('all')}
          >All</button>

          <select
            value={filterClient}
            onChange={e => setFilterClient(e.target.value)}
            className="w-48 p-2 rounded bg-[#2A1C4A] text-white"
          >
            <option value="">All Clients</option>
            {clients.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>

          <button
            onClick={() => navigate('/shifts/new')}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded"
          >
            + Book Shift
          </button>
        </div>

        {/* Search */}
        <div className="mb-4 flex justify-end">
          <input
            type="text"
            placeholder="Search by client name..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="p-2 rounded bg-[#2A1C4A] text-white w-full sm:w-64 border border-[#3B2D5F] placeholder-gray-400"
          />
        </div>

        {/* DataTable */}
        <DataTable
          columns={columns}
          data={filteredShifts}
          pagination
          paginationPerPage={10}
          customStyles={{
            headCells: {
              style: {
                backgroundColor: '#1C1235',
                color: 'white'
              }
            },
            rows: {
              style: {
                backgroundColor: '#1C1235',
                color: 'white'
              }
            },
            pagination: {
              style: {
                backgroundColor: '#1C1235',
                color: 'white'
              }
            }
          }}
        />
      </div>
       {/* Modal for client detail */}
                    {selectedClient && (
                      <Modal
                        isOpen={true}
                        onRequestClose={() => setSelectedClient(null)}
                        className="max-w-lg mx-auto mt-24 bg-[#1C1235] p-6 rounded-xl outline-none shadow-xl text-white"
                        overlayClassName="fixed inset-0 bg-black bg-opacity-60 flex items-start justify-center z-50"
                      >
                        <h2 className="text-xl font-bold mb-4">{selectedClient.name}</h2>
                        <p><strong>Phone:</strong> {selectedClient.phone}</p>
                        <p><strong>Address:</strong> {selectedClient.address}</p>
                        <p><strong>DOB:</strong> {selectedClient.dob}</p>
                        <p><strong>NDIS:</strong> {selectedClient.ndisNumber}</p>
                        <p><strong>About:</strong> {selectedClient.about}</p>
                        <p><strong>Allergies:</strong> {selectedClient.allergies}</p>
                        <p><strong>Medications:</strong> {selectedClient.medications}</p>
                        <p><strong>Emergency Contact:</strong> {selectedClient.emergencyContact}</p>
                        <p><strong>Goals:</strong> {selectedClient.goals}</p>
                        <p><strong>Other Info:</strong> {selectedClient.otherInfo}</p>
                        <p><strong>Plan Type:</strong> {selectedClient.planType}</p>
                        {selectedClient.documents && (
                                  <p>
                                    <strong>Documents: </strong>
                                    {(Array.isArray(selectedClient.documents)
                                      ? selectedClient.documents
                                      : JSON.parse(selectedClient.documents)
                                    ).map((doc, idx) => (
                                      <a
                                        key={idx}
                                        href={`${API_BASE_URL}${doc}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-400 hover:text-purple-200"
                                      >
                                        Link {idx + 1}{' '}
                                      </a>
                                    ))}
                                  </p>
                                )}

              
                        <div className="text-right mt-4">
                          <button
                            onClick={() => setSelectedClient(null)}
                            className="bg-red-600 hover:bg-red-700 px-4 py-2 rounded"
                          >
                            Close
                          </button>
                        </div>
                      </Modal>
                    )}
    </div>
  );
};

export default ShiftList;
